import * as React from 'react';
import { Box, Fade, Tooltip } from '@mui/material';
import { TooltipProps } from '@mui/material/Tooltip';
import { ContentCopy } from '@mui/icons-material';
import { CopyToClipboard } from './CopyToClipBoard';

export type CutterTooltipSeverity = 'info' | 'warning' | 'error' | 'success';

interface CutterTooltipProps extends TooltipProps {
    severity?: CutterTooltipSeverity;
    /** When set, a copy-to-clipboard icon is rendered inside the tooltip bubble. Falls back to `title` when it is a plain string. */
    copyText?: string;
}

const severityStyles: Record<CutterTooltipSeverity, { border: string; bg: string }> = {
    info:    { border: '#29b6f6', bg: 'rgba(0, 40, 70, 0.97)' },
    warning: { border: '#ffa726', bg: 'rgba(50, 25, 0, 0.97)' },
    error:   { border: '#ef5350', bg: 'rgba(60, 0, 0, 0.97)' },
    success: { border: '#66bb6a', bg: 'rgba(0, 50, 10, 0.97)' },
};

/**
 * CutterTooltip — styled Tooltip for cutter-specific information, warnings, and errors.
 * Use `severity` to match the semantic meaning of the content (default: 'info').
 * Set `copyText` to add a copy-to-clipboard icon inside the tooltip bubble (falls back to `title` when it is a plain string).
 * Distinct from HelpTooltip which is reserved for application-level help content.
 */
const CutterTooltip: React.FC<CutterTooltipProps> = ({ children, severity = 'success', copyText, slots, slotProps, ...props }) => {
    const { border, bg } = severityStyles[severity];

    const resolvedCopyText = copyText ?? (typeof props.title === 'string' ? props.title : undefined);

    const titleContent = resolvedCopyText != null
        ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Box sx={{ flex: 1 }}>{props.title}</Box>
                <CopyToClipboard
                    TextToCopy={resolvedCopyText}
                    AckMessage="Copied to clipboard"
                    size="small"
                    sx={{ color: 'inherit', opacity: 0.7, p: 0.25, flexShrink: 0, '&:hover': { opacity: 1 } }}
                >
                    <ContentCopy sx={{ fontSize: '0.85rem' }} />
                </CopyToClipboard>
            </Box>
        )
        : props.title;

    return (
        <Tooltip
            arrow
            enterDelay={200}
            leaveDelay={0}
            {...props}
            title={titleContent}
            slots={{ transition: Fade, ...slots }}
            slotProps={{
                ...slotProps,
                transition: { timeout: 300, ...(slotProps?.transition as object) },
                tooltip: {
                    ...slotProps?.tooltip,
                    sx: {
                        bgcolor: bg,
                        color: 'white',
                        boxShadow: `0 4px 20px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.08)`,
                        borderLeft: `3px solid ${border}`,
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        maxWidth: 450,
                        p: 1.75,
                        '& .MuiTooltip-arrow': {
                            color: bg,
                        },
                        ...(slotProps?.tooltip as { sx?: object })?.sx,
                    },
                },
            }}
        >
            {children}
        </Tooltip>
    );
};

export default CutterTooltip;
